import {
  FETCH_INVENTORY,
  FETCH_INVENTORY_SUCCESS,
  FETCH_INVENTORY_FAILED,
  EDIT_INVENTORY,
} from '../store/types';
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, set, remove, push, update, get, runTransaction } from 'firebase/database';
import { uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { eachDayKey } from '../other/PartnerUtils';

const snapshotToArray = (snap) => {
  const val = snap.val();
  if (!val) return [];
  return Object.keys(val).map((id) => ({ ...val[id], id }));
};

export const fetchInventory = (partnerId) => (dispatch) => {
  const { inventoryRef } = firebase;
  dispatch({ type: FETCH_INVENTORY, payload: { partnerId } });
  onValue(inventoryRef(partnerId), (snap) => {
    if (snap.val()) {
      dispatch({
        type: FETCH_INVENTORY_SUCCESS,
        payload: { partnerId, items: snapshotToArray(snap) },
      });
    } else {
      dispatch({
        type: FETCH_INVENTORY_FAILED,
        payload: {
          partnerId,
          msg:
            (store.getState().languagedata?.defaultLanguage &&
              store.getState().languagedata.defaultLanguage.no_data) ||
            'No inventory',
        },
      });
    }
  });
};

const uploadItemImage = async (partnerId, itemId, n, image) => {
  const { inventoryImageRef } = firebase;
  await uploadBytesResumable(inventoryImageRef(partnerId, itemId, n), image);
  return getDownloadURL(inventoryImageRef(partnerId, itemId, n));
};

export const editInventoryItem = (item, method) => async (dispatch) => {
  const { inventoryRef, inventoryItemRef } = firebase;
  dispatch({ type: EDIT_INVENTORY, payload: { method, item } });

  const partnerId = item.partnerId;
  if (!partnerId) throw new Error('partnerId is required');

  if (method === 'Add') {
    const ref = await push(inventoryRef(partnerId), {
      ...item,
      active: item.active !== false,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
    return ref.key;
  }
  if (method === 'Delete') {
    await remove(inventoryItemRef(partnerId, item.id));
    return item.id;
  }
  if (method === 'AddImage') {
    const url = await uploadItemImage(partnerId, item.id, item.imageIndex, item.imageFile);
    await update(inventoryItemRef(partnerId, item.id), {
      [`images/${item.imageIndex}`]: url,
      updatedAt: Date.now(),
    });
    return url;
  }
  // Update
  await set(inventoryItemRef(partnerId, item.id), {
    ...item,
    updatedAt: Date.now(),
  });
  return item.id;
};

export const fetchAvailability = (partnerId, itemId) => async () => {
  const { availabilityRef } = firebase;
  const snap = await get(availabilityRef(partnerId, itemId));
  return snap.val() || {};
};

export const reserveAvailability = (partnerId, itemId, checkIn, checkOut, units = 1) => async () => {
  const { availabilityDayRef, inventoryItemRef } = firebase;
  const itemSnap = await get(inventoryItemRef(partnerId, itemId));
  const item = itemSnap.val();
  if (!item) throw new Error('Inventory item not found');
  const total = Number(item.unitsAvailable || 1);

  const days = eachDayKey(checkIn, checkOut);
  for (const day of days) {
    // eslint-disable-next-line no-await-in-loop
    const result = await runTransaction(availabilityDayRef(partnerId, itemId, day), (current) => {
      const used = Number(current || 0);
      if (used + units > total) return current; // abort
      return used + units;
    });
    if (!result.committed) {
      // rollback previous days
      for (const d of days) {
        if (d === day) break;
        // eslint-disable-next-line no-await-in-loop
        await runTransaction(availabilityDayRef(partnerId, itemId, d), (current) => {
          const used = Number(current || 0);
          return Math.max(0, used - units);
        });
      }
      throw new Error('No availability for selected dates');
    }
  }
  return true;
};

export const releaseAvailability = (partnerId, itemId, checkIn, checkOut, units = 1) => async () => {
  const { availabilityDayRef } = firebase;
  const days = eachDayKey(checkIn, checkOut);
  for (const day of days) {
    // eslint-disable-next-line no-await-in-loop
    await runTransaction(availabilityDayRef(partnerId, itemId, day), (current) => {
      const used = Number(current || 0);
      return Math.max(0, used - units);
    });
  }
  return true;
};
